import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const cx = (...c) => c.filter(Boolean).join(" ");

const ComponentTitle = ({
  title,
  highlight,
  subtitle,
  align = "center",
  size = "xl",
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (reduceMotion) {
      setIsVisible(true);
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const alignCls =
    align === "left"
      ? "text-left items-start"
      : align === "right"
      ? "text-right items-end"
      : "text-center items-center";

  const sizeCls =
    size === "lg"
      ? "text-3xl sm:text-4xl lg:text-5xl"
      : size === "md"
      ? "text-2xl sm:text-3xl lg:text-4xl"
      : "text-4xl sm:text-5xl lg:text-6xl xl:text-7xl";

  return (
    <div
      ref={ref}
      className={cx(
        "flex flex-col gap-3",
        alignCls,
        "transition-all duration-700 will-change-transform",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
        className
      )}
    >
      {/* petit “eyebrow” décoratif */}
      <div className="flex items-center gap-2">
        <span className="h-[2px] w-10 bg-gradient-to-r from-yellow-400 to-orange-600 rounded-full" />
        <span className="text-xs uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
          MonAppliClub
        </span>
      </div>

      <h2
        className={cx(
          sizeCls,
          "font-semibold font-passionOne",
          "text-slate-900 dark:text-slate-100 leading-[1.02]"
        )}
      >
        {title}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-600">
          {highlight}
        </span>
      </h2>

      {/* underline glow */}
      <div className="relative">
        <span className="block h-[3px] w-24 rounded-full bg-gradient-to-r from-yellow-400 to-orange-600" />
        <span className="absolute left-0 top-0 h-[3px] w-24 rounded-full blur-md opacity-60 bg-gradient-to-r from-yellow-400 to-orange-600" />
      </div>

      {subtitle && (
        <p className="max-w-2xl text-base sm:text-lg text-slate-600 dark:text-slate-300">
          {subtitle}
        </p>
      )}
    </div>
  );
};

ComponentTitle.propTypes = {
  title: PropTypes.string.isRequired,
  highlight: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  align: PropTypes.oneOf(["left", "center", "right"]),
  size: PropTypes.oneOf(["md", "lg", "xl"]),
  className: PropTypes.string,
};

export default ComponentTitle;
